/**
 * C8 — the MCP page, as AgencyAnalytics': the server address to paste into
 * an AI tool, how to add it, and the person's connected apps with
 * Disconnect. Opened as #/mcp/authorize?… it is the consent screen instead.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { MCP_SERVER_URL } from '../../api.ts';
import type { Client } from '../../reports/reports-api.ts';
import { Page } from '../AgencyPages.tsx';
import { consentQuery, mcpApi, type McpConnection } from './mcp-api.ts';
import { McpConsent } from './McpConsent.tsx';

const when = new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });

function Connections({ organisationId }: { organisationId: string }): ReactNode {
  const [rows, setRows] = useState<McpConnection[] | null>(null);
  const load = () => mcpApi.connections(organisationId).then(setRows, () => setRows([]));
  useEffect(() => void load(), [organisationId]);
  if (rows === null) return <p className="as-empty">Loading…</p>;
  if (rows.length === 0) return <p className="as-empty">No AI tools are connected yet.</p>;
  return (
    <table className="ag-table">
      <thead>
        <tr>
          <th scope="col">App</th>
          <th scope="col">Client</th>
          <th scope="col">Connected</th>
          <th scope="col">
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.app_id}-${row.client_id}`}>
            <td>{row.app_name}</td>
            <td>{row.client_name}</td>
            <td>{when.format(new Date(row.connected_at))}</td>
            <td>
              <button
                type="button"
                className="ex-link"
                onClick={() =>
                  void mcpApi.disconnect(organisationId, row.app_id, row.client_id).then(setRows)
                }
              >
                Disconnect
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function McpPage(props: { organisationId: string; clients: Client[] }): ReactNode {
  const query = consentQuery();
  const [copied, setCopied] = useState(false);
  if (query)
    return (
      <Page title="Connect an AI tool">
        <McpConsent organisationId={props.organisationId} clients={props.clients} query={query} />
      </Page>
    );
  const copy = () => void navigator.clipboard.writeText(MCP_SERVER_URL).then(() => setCopied(true));
  return (
    <Page title="MCP">
      <section className="card">
        <h2>Connect your AI tools</h2>
        <p>
          Ask Claude, ChatGPT or any MCP-ready tool about a client’s results. Each connection reads
          one client, as you, and you can disconnect it here at any time.
        </p>
        <div className="mcp-url">
          <code>{MCP_SERVER_URL}</code>
          <button type="button" className="btn secondary" onClick={copy}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <ol className="mcp-steps">
          <li>In your AI tool, add a custom connector (or MCP server) with this address.</li>
          <li>Sign in to SynAnalytics when it asks, and choose the client.</li>
          <li>Ask away: “How did last month compare with the month before?”</li>
        </ol>
      </section>
      <section className="card">
        <h2>Connected apps</h2>
        <Connections organisationId={props.organisationId} />
      </section>
    </Page>
  );
}
